import {Injectable} from '@angular/core';
import {Albaran} from "../models/albaran";
import {Matricula} from "../models/matricula";
import {todayLocal} from "./date-helper";

/**
 * Copia local de los datos de trabajo del día (lista de albaranes y ficha de cada uno), para que
 * el xofer sepa qué lleva aunque abra la app sin cobertura. Es solo de lectura: lo que haga
 * (checks, incidencias, fotos) sigue necesitando conexión.
 *
 * Se guarda cada vez que los datos llegan del servidor y solo se sustituye entonces, así que
 * siempre queda la última copia buena, con la hora en que se descargó para saber si es actual.
 */
export interface AlbaranesBackup {
    date: string;          // día (Y-m-d) al que corresponde la lista
    downloadedAt: string;  // ISO de cuándo se descargó
    nomXofer: string;
    matriculaXofer: string;
    matriculas: Matricula[];
    albaranes: Albaran[];
    tareasHechasTotales: number;
    tareasPendientesTotales: number;
}

export interface AlbaranInfoBackup {
    downloadedAt: string;
    info: any;
}

@Injectable({
    providedIn: 'root'
})
export class DataCacheService {

    private static readonly LIST_KEY = 'albaranesBackup';
    private static readonly INFO_KEY = 'albaranInfoBackup';

    saveAlbaranes(backup: Omit<AlbaranesBackup, 'date' | 'downloadedAt'>): void {
        const stored: AlbaranesBackup = {
            ...backup,
            date: todayLocal(),
            downloadedAt: new Date().toISOString(),
        };

        this.write(DataCacheService.LIST_KEY, stored);
    }

    getAlbaranes(): AlbaranesBackup | null {
        return this.read<AlbaranesBackup>(DataCacheService.LIST_KEY);
    }

    // Las fichas se guardan por albarán; se conservan solo las de la copia de la lista vigente,
    // para no acumular albaranes de días pasados.
    saveAlbaranInfo(idAlbara: any, info: any): void {
        const all = this.read<{ [id: string]: AlbaranInfoBackup }>(DataCacheService.INFO_KEY) ?? {};

        all[String(idAlbara)] = {downloadedAt: new Date().toISOString(), info: info};

        this.write(DataCacheService.INFO_KEY, all);
    }

    getAlbaranInfo(idAlbara: any): AlbaranInfoBackup | null {
        const all = this.read<{ [id: string]: AlbaranInfoBackup }>(DataCacheService.INFO_KEY);

        return all ? (all[String(idAlbara)] ?? null) : null;
    }

    /** Quita las fichas de albaranes que ya no están en la lista guardada. */
    pruneAlbaranInfo(albaranes: Albaran[]): void {
        const all = this.read<{ [id: string]: AlbaranInfoBackup }>(DataCacheService.INFO_KEY);

        if (!all) return;

        const alive = new Set(albaranes.map(albaran => String(albaran.id)));
        const kept: { [id: string]: AlbaranInfoBackup } = {};

        for (const id of Object.keys(all)) {
            if (alive.has(id)) {
                kept[id] = all[id];
            }
        }

        this.write(DataCacheService.INFO_KEY, kept);
    }

    clear(): void {
        localStorage.removeItem(DataCacheService.LIST_KEY);
        localStorage.removeItem(DataCacheService.INFO_KEY);
    }

    /** "del 24/09/2026, descargada el 24/09 a las 09:15" para el aviso de la pantalla. */
    describe(backup: { date?: string, downloadedAt: string }): string {
        const pad = (n: number) => String(n).padStart(2, '0');
        const at = new Date(backup.downloadedAt);
        const when = pad(at.getDate()) + '/' + pad(at.getMonth() + 1) + ' a las ' + pad(at.getHours()) + ':' + pad(at.getMinutes());

        if (!backup.date) {
            return 'descargada el ' + when;
        }

        const [year, month, day] = backup.date.split('-');

        return 'del ' + day + '/' + month + '/' + year + ', descargada el ' + when;
    }

    private write(key: string, value: any): void {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            // Almacenamiento lleno: la copia es una ayuda, no puede tumbar la pantalla
        }
    }

    private read<T>(key: string): T | null {
        const stored = localStorage.getItem(key);

        if (!stored) return null;

        try {
            return JSON.parse(stored);
        } catch (error) {
            return null;
        }
    }
}
